import * as React from 'react'
import { useRouter } from 'next/router'
import NextLink from 'next/link'

import analytics from '../../../util/analytics-data-attributes/analyticsAttributes'
import type { Action, Category } from '../../../util/analytics-data-attributes/analyticsAttributes'
import { ElementRole } from '../../types'

export type Props = {
  href: string,
  children: React.ReactNode,
  onClick?: React.MouseEventHandler<HTMLAnchorElement>,
  as?: string,
  className?: string,
  isSelected?: boolean,
  replace?: boolean,
  scroll?: boolean,
  shallow?: boolean,
  role?: ElementRole,
  dataAnalyticsAction?: Action,
  dataAnalyticsCategory?: Category,
  dataAnalyticsLabel?: string,
}

/**
 * Link for client side transitions
 * @param {object} props Component props
 * @param {string} props.href The path or URL to navigate to. This is the only required prop
 * @param {object|string} props.children The content of the link
 * @param {function} props.onClick On Click callback function for link
 * @param {string} props.as Optional decorator for the path that will be shown in the browser URL bar.
 * @param {boolean} props.replace Replace the current history state instead of adding a new url into the stack.
 * @param {boolean} props.scroll Scroll to the top of the page after a navigation. Defaults to true
 * @param {boolean} props.shallow Update the path of the current page without rerunning
 * @param {boolean} props.isSelected Is the link for the current page
 * @param {Action} props.dataAnalyticsAction The action to be tracked by Google Analytics
 * @param {Category} props.dataAnalyticsCategory The category to be tracked by Google Analytics
 * @param {string} props.dataAnalyticsLabel The label to be tracked by Google Analytics
 */

const Link: React.FunctionComponent<Props> = ({
  href,
  children,
  onClick,
  as,
  className,
  isSelected,
  replace,
  scroll,
  shallow,
  role = 'tab',
  dataAnalyticsAction,
  dataAnalyticsCategory,
  dataAnalyticsLabel,
  ...rest
}) => (
  <NextLink
    {...{
      href,
      onClick,
      as,
      className,
      'aria-selected': isSelected,
      replace,
      scroll,
      shallow,
      role,
      ...analytics({
        router: useRouter(),
        action: dataAnalyticsAction,
        category: dataAnalyticsCategory,
        label: dataAnalyticsLabel,
      }),
      ...rest
    }}
  >
    {children}
  </NextLink>
)

Link.defaultProps = {
  as: undefined,
  className: undefined,
  dataAnalyticsAction: undefined,
  dataAnalyticsCategory: undefined,
  dataAnalyticsLabel: undefined,
  isSelected: false,
  onClick: undefined,
  replace: false,
  scroll: true,
  shallow: false,
}

export default Link
